import {TextAnchorProperty} from 'csstype';
import * as d3 from 'd3';
import _ from 'underscore';

import {Strand} from 'constants/MolbioConstants';
import arcs from 'molbio/maps/circular/Arcs';
import {Point} from 'util/geometry/GeometryConstants';
import {getAngle, polarToCartesian} from 'util/geometry/GeometryUtil';

/**
 * Gets the increment between each axis numbering label for a sequence, as close to the given ideal
 * number of labels as possible.
 */
export function axisSpacing(rangeLength: number, idealNumLabels: number = 8): number {
  const targetSpacing = rangeLength / idealNumLabels;
  const pow10 = Math.pow(10, Math.floor(Math.log(targetSpacing) / Math.LN10) + 1); // power of 10 larger than range.
  const spacing = _.chain([pow10 / 4, pow10 / 2, pow10])
    .filter((x) => x % 1 === 0)
    .min((x) => Math.abs(x - targetSpacing))
    .value();
  return spacing;
}

/**
 * Gets the cartesian point on a circle representing a sequence given the position on the sequence.
 * @param circleCenter Cartesian point of the circle center
 * @param index Zero-indexed position of the point to find on the sequence
 * @param length Length represented by the entire circle
 * @param radius Radius of the circle
 */
export function getCirclePoint(circleCenter: Point, index: number, length: number, radius: number): Point {
  return polarToCartesian(circleCenter, radius, getAngle(index, length));
}

/**
 * Gets the text anchor for text rendered at a point on a circle.
 */
export function getTextAnchorForCircularText(circleCenter: Point, textPoint: Point): TextAnchorProperty {
  if (textPoint.x > circleCenter.x) {
    return 'start';
  } else {
    return 'end';
  }
}

/**
 * Gets the arc function for drawing a feature of the given strand.
 */
export function getArcForStrand(strand: Strand): d3.Arc<unknown, unknown> {
  switch (strand) {
    case 0:
      return d3.arc();
    case -1:
      return arcs.backwardArc();
    case 1:
    default:
      return arcs.forwardArc();
  }
}
